/**
 * Places a popover under (or, without room, above) its field, outside any scrolling pane that would clip it.
 *
 *   const style = useFloating(open, fieldRef, panelRef, { minWidth: 256 });
 *   {open && createPortal(<div ref={panelRef} style={style}>...</div>, document.body)}
 *
 * The panel is position: fixed, follows the field on scroll and resize, and never leaves the viewport sideways.
 */
import { CSSProperties, RefObject, useLayoutEffect, useState } from "react";

const GAP = 6;
const MARGIN = 8;

export function useFloating(open: boolean, anchor: RefObject<HTMLElement | null>, panel: RefObject<HTMLElement | null>, opts: { minWidth?: number; matchWidth?: boolean } = {}): CSSProperties {
  const { minWidth = 0, matchWidth = true } = opts;
  const [style, setStyle] = useState<CSSProperties>({ position: "fixed", top: -9999, left: 0, visibility: "hidden" });

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const a = anchor.current?.getBoundingClientRect();
      const p = panel.current;
      if (!a) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const width = Math.min(Math.max(matchWidth ? a.width : 0, minWidth, p?.offsetWidth && !matchWidth ? p.offsetWidth : 0), vw - MARGIN * 2);
      const height = p?.offsetHeight ?? 0;
      const below = vh - a.bottom - GAP - MARGIN;
      const above = a.top - GAP - MARGIN;
      const up = height > below && above > below;
      const left = Math.min(Math.max(MARGIN, a.left), vw - width - MARGIN);
      setStyle({
        position: "fixed",
        left,
        width: matchWidth || minWidth ? width : undefined,
        top: up ? undefined : a.bottom + GAP,
        bottom: up ? vh - a.top + GAP : undefined,
        maxHeight: Math.max(160, up ? above : below),
        zIndex: 60,
      });
    };
    place();
    // Once more after the panel has rendered and has a height.
    const raf = requestAnimationFrame(place);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, anchor, panel, minWidth, matchWidth]);

  return style;
}
